import { useState, type CSSProperties, type ReactNode } from 'react';
import { Icon, tokens } from '@/design';
import type { LayoutGraphicPatch } from './layoutDocument';
import { LayoutTextSymbolPanel } from './LayoutTextSymbolPanel';
import { SymbolColorPicker } from './SymbolColorPicker';
import { MM_PER_INCH } from './pageUnits';
import type {
  LayoutGraphicElement,
  LayoutGraphicStroke,
  PageRect,
  PageUnits,
} from './layoutTypes';

export interface GraphicsPropertiesPanelProps {
  readonly graphic: LayoutGraphicElement;
  readonly pageWidthMm: number;
  readonly pageHeightMm: number;
  readonly pageUnits: PageUnits;
  readonly onChange: (patch: LayoutGraphicPatch) => void;
  readonly onRectChange: (rect: PageRect) => void;
  readonly onDelete: () => void;
  readonly onClose: () => void;
}

type Tab = 'options' | 'symbol' | 'placement';

const MARKER_SHAPES = ['circle', 'square', 'diamond', 'triangle', 'cross', 'x'] as const;
const STROKE_STYLES = [
  ['solid', 'Solid'],
  ['dash', 'Dash'],
  ['dot', 'Dot'],
  ['dashDot', 'Dash dot'],
] as const;

function bounded(raw: string, min: number, max: number, fallback: number): number {
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? Math.max(min, Math.min(max, parsed)) : fallback;
}

function Section({
  title,
  open = false,
  children,
}: {
  readonly title: string;
  readonly open?: boolean;
  readonly children: ReactNode;
}) {
  return (
    <details open={open}>
      <summary
        style={{
          minHeight: tokens.density.rowDense,
          cursor: 'pointer',
          fontSize: tokens.font.size.md,
          fontWeight: tokens.font.weight.semibold,
        }}
      >
        {title}
      </summary>
      <div
        style={{
          display: 'grid',
          gap: tokens.space[2],
          padding: `0 0 ${tokens.space[3]}px ${tokens.space[5]}px`,
        }}
      >
        {children}
      </div>
    </details>
  );
}

export function GraphicsPropertiesPanel({
  graphic,
  pageWidthMm,
  pageHeightMm,
  pageUnits,
  onChange,
  onRectChange,
  onDelete,
  onClose,
}: GraphicsPropertiesPanelProps) {
  const [tab, setTab] = useState<Tab>('options');
  const [editingTextSymbol, setEditingTextSymbol] = useState(false);
  const [preserveAspect, setPreserveAspect] = useState(true);
  const disabled = graphic.locked;
  const unitFactor = pageUnits === 'in' ? MM_PER_INCH : 1;
  const unitLabel = pageUnits === 'in' ? 'in' : 'mm';

  const field: CSSProperties = {
    width: '100%',
    minHeight: tokens.density.rowDense,
    boxSizing: 'border-box',
    padding: `0 ${tokens.space[2]}px`,
    border: tokens.border.thin,
    borderRadius: tokens.radius.input,
    background: tokens.color.surface[0],
    color: tokens.color.ink.primary,
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.sm,
  };
  const row: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: `${tokens.density.touchMin * 2.8}px minmax(0, 1fr)`,
    alignItems: 'center',
    gap: tokens.space[2],
    fontSize: tokens.font.size.sm,
  };
  const check: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.space[2],
    fontSize: tokens.font.size.sm,
  };
  const iconButton: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: tokens.density.rowDense,
    minHeight: tokens.density.rowDense,
    padding: 0,
    border: 0,
    background: 'transparent',
    color: tokens.color.ink.secondary,
    cursor: 'pointer',
  };
  const tabStyle = (active: boolean): CSSProperties => ({
    flex: 1,
    minHeight: tokens.density.rowDefault,
    border: 0,
    borderBottom: `${tokens.focus.ringWidth}px solid ${
      active ? tokens.color.semantic.info : 'transparent'
    }`,
    background: 'transparent',
    color: active ? tokens.color.semantic.info : tokens.color.ink.secondary,
    cursor: 'pointer',
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.sm,
  });
  const numberField = (
    label: string,
    value: number,
    min: number,
    max: number,
    onValue: (value: number) => void,
    step = 0.5,
  ) => (
    <label style={row}>
      <span>{label}</span>
      <input
        aria-label={label}
        type="number"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        onChange={(event) => onValue(bounded(event.target.value, min, max, value))}
        style={field}
      />
    </label>
  );
  const colorField = (
    label: string,
    value: string | null,
    onValue: (value: string | null) => void,
  ) => (
    <div style={row}>
      <span>{label}</span>
      <SymbolColorPicker label={label} value={value} disabled={disabled} onChange={onValue} />
    </div>
  );
  const strokeFields = (
    stroke: LayoutGraphicStroke,
    patch: (value: LayoutGraphicPatch['stroke']) => void,
  ) => (
    <>
      {colorField('Line color', stroke.color, (color) => patch({ color }))}
      {numberField('Line width', stroke.widthPt, 0, 100, (widthPt) => patch({ widthPt }))}
      <label style={row}>
        <span>Line style</span>
        <select
          aria-label="Line style"
          value={stroke.style}
          disabled={disabled}
          onChange={(event) => patch({
            style: event.target.value as typeof stroke.style,
          })}
          style={field}
        >
          {STROKE_STYLES.map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </label>
      {numberField('Line opacity', stroke.opacityPercent, 0, 100, (opacityPercent) =>
        patch({ opacityPercent }), 1)}
      <label style={row}>
        <span>Cap</span>
        <select
          aria-label="Line cap"
          value={stroke.cap}
          disabled={disabled}
          onChange={(event) => patch({ cap: event.target.value as typeof stroke.cap })}
          style={field}
        >
          <option value="butt">Butt</option>
          <option value="round">Round</option>
          <option value="square">Square</option>
        </select>
      </label>
      <label style={row}>
        <span>Join</span>
        <select
          aria-label="Line join"
          value={stroke.join}
          disabled={disabled}
          onChange={(event) => patch({ join: event.target.value as typeof stroke.join })}
          style={field}
        >
          <option value="miter">Miter</option>
          <option value="round">Round</option>
          <option value="bevel">Bevel</option>
        </select>
      </label>
    </>
  );
  const fillRuleField = (
    fill: Extract<LayoutGraphicElement, { category: 'polygon' }>['fill'],
  ) => (
    <label style={row}>
      <span>Fill rule</span>
      <select
        aria-label="Fill rule"
        value={fill.rule}
        disabled={disabled}
        onChange={(event) => onChange({
          fill: { rule: event.target.value as typeof fill.rule },
        })}
        style={field}
      >
        <option value="nonzero">Nonzero</option>
        <option value="evenodd">Even-odd</option>
      </select>
    </label>
  );

  const placementValue = (valueMm: number) => Number((valueMm / unitFactor).toFixed(3));
  const setPlacement = (key: keyof PageRect, displayValue: number) => {
    const valueMm = displayValue * unitFactor;
    const next = { ...graphic.rect, [key]: valueMm };
    if (preserveAspect && (key === 'width' || key === 'height')) {
      const ratio = graphic.rect.width / Math.max(graphic.rect.height, 0.1);
      if (key === 'width') next.height = valueMm / ratio;
      else next.width = valueMm * ratio;
    }
    onRectChange(next);
  };

  if (editingTextSymbol && graphic.category === 'text') {
    return (
      <aside
        data-testid="graphics-properties-panel"
        aria-label="Graphic properties"
        style={{
          position: 'absolute',
          zIndex: tokens.zIndex.dock,
          top: tokens.space[3],
          right: tokens.space[3],
          width: tokens.layout.popoverLg,
          maxHeight: `calc(100% - ${tokens.space[6]}px)`,
          overflowY: 'auto',
          padding: tokens.space[4],
          border: tokens.border.thin,
          borderRadius: tokens.radius.md,
          background: tokens.color.surface[1],
          boxShadow: tokens.elevation[2],
        }}
      >
        <LayoutTextSymbolPanel
          value={graphic.textSymbol}
          heading="Text Symbol"
          testId="graphic-text-symbol-panel"
          onApply={(textSymbol) => onChange({ textSymbol })}
          onBack={() => setEditingTextSymbol(false)}
        />
      </aside>
    );
  }

  return (
    <aside
      data-testid="graphics-properties-panel"
      aria-label="Graphic properties"
      style={{
        position: 'absolute',
        zIndex: tokens.zIndex.dock,
        top: tokens.space[3],
        right: tokens.space[3],
        width: tokens.layout.popoverLg,
        maxHeight: `calc(100% - ${tokens.space[6]}px)`,
        overflowY: 'auto',
        padding: tokens.space[4],
        border: tokens.border.thin,
        borderRadius: tokens.radius.md,
        background: tokens.color.surface[1],
        boxShadow: tokens.elevation[2],
        color: tokens.color.ink.primary,
        fontFamily: tokens.font.family.ui,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: tokens.space[2] }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: tokens.color.semantic.info, fontSize: tokens.font.size.lg }}>
            Format Graphic
          </div>
          <div style={{ color: tokens.color.ink.secondary, fontSize: tokens.font.size.sm }}>
            {graphic.name}
          </div>
        </div>
        <button
          type="button"
          aria-label="Delete graphic"
          disabled={disabled}
          onClick={onDelete}
          style={{ ...iconButton, color: disabled ? tokens.color.ink.muted : tokens.color.semantic.danger }}
        >
          <Icon name="delete" size={20} decorative />
        </button>
        <button type="button" aria-label="Close graphic properties" onClick={onClose} style={iconButton}>
          <Icon name="close" size={20} decorative />
        </button>
      </div>

      <div role="tablist" aria-label="Graphic property sections" style={{ display: 'flex' }}>
        {([
          ['options', 'Options'],
          ['symbol', graphic.category === 'text' ? 'Text Symbol' : 'Symbol'],
          ['placement', 'Placement'],
        ] as const).map(([id, label]) => (
          <button
            key={id}
            id={`graphic-properties-tab-${id}`}
            type="button"
            role="tab"
            aria-selected={tab === id}
            aria-controls={`graphic-properties-panel-${id}`}
            tabIndex={tab === id ? 0 : -1}
            onClick={() => setTab(id)}
            onKeyDown={(event) => {
              const tabs: readonly Tab[] = ['options', 'symbol', 'placement'];
              const direction = event.key === 'ArrowRight' ? 1
                : event.key === 'ArrowLeft' ? -1 : 0;
              if (!direction) return;
              event.preventDefault();
              const next = tabs[(tabs.indexOf(id) + direction + tabs.length) % tabs.length];
              setTab(next);
              globalThis.document
                .getElementById(`graphic-properties-tab-${next}`)
                ?.focus();
            }}
            style={tabStyle(tab === id)}
          >
            {label}
          </button>
        ))}
      </div>

      <div
        id={`graphic-properties-panel-${tab}`}
        role="tabpanel"
        aria-labelledby={`graphic-properties-tab-${tab}`}
        style={{ display: 'grid', gap: tokens.space[2], paddingTop: tokens.space[3] }}
      >
        {tab === 'options' ? (
          <>
            <Section title="General" open>
              <label style={row}>
                <span>Name</span>
                <input
                  aria-label="Graphic name"
                  value={graphic.name}
                  disabled={disabled}
                  onChange={(event) => onChange({ name: event.target.value })}
                  style={field}
                />
              </label>
              <label style={check}>
                <input
                  type="checkbox"
                  checked={graphic.visible}
                  disabled={disabled}
                  onChange={(event) => onChange({ visible: event.target.checked })}
                />
                Visible
              </label>
              <label style={check}>
                <input
                  aria-label="Lock graphic"
                  type="checkbox"
                  checked={graphic.locked}
                  onChange={(event) => onChange({ locked: event.target.checked })}
                />
                Locked
              </label>
            </Section>
            {graphic.category === 'text' ? (
              <Section title="Text" open>
                <label style={{ display: 'grid', gap: tokens.space[1], fontSize: tokens.font.size.sm }}>
                  <span>Content</span>
                  <textarea
                    aria-label="Text content"
                    value={graphic.text.text}
                    disabled={disabled}
                    rows={4}
                    onChange={(event) => onChange({ text: { text: event.target.value } })}
                    style={{ ...field, padding: tokens.space[2], resize: 'vertical' }}
                  />
                </label>
                <label style={row}>
                  <span>Fitting</span>
                  <select
                    aria-label="Text fitting behavior"
                    value={graphic.text.overflow}
                    disabled={disabled}
                    onChange={(event) => onChange({
                      text: { overflow: event.target.value as typeof graphic.text.overflow },
                    })}
                    style={field}
                  >
                    <option value="visible">None</option>
                    <option value="shrink">Shrink text</option>
                    <option value="clip">Clip</option>
                  </select>
                </label>
                {(graphic.graphicType === 'rectangleText'
                  || graphic.graphicType === 'polygonText') ? (
                  <>
                    {numberField('Columns', graphic.text.columnCount, 1, 12, (columnCount) =>
                      onChange({ text: { columnCount } }), 1)}
                    {numberField('Column gap', graphic.text.columnGapPt, 0, 144, (columnGapPt) =>
                      onChange({ text: { columnGapPt } }))}
                    {numberField('Text margin', graphic.text.marginPt, 0, 144, (marginPt) =>
                      onChange({ text: { marginPt } }))}
                  </>
                ) : null}
                {(graphic.graphicType === 'curvedText'
                  || graphic.graphicType === 'circleText'
                  || graphic.graphicType === 'ellipseText') ? (
                  <>
                    {numberField('Path offset', graphic.text.pathOffset, -100, 100, (pathOffset) =>
                      onChange({ text: { pathOffset } }))}
                    <label style={check}>
                      <input
                        type="checkbox"
                        checked={graphic.text.reversePath}
                        disabled={disabled}
                        onChange={(event) => onChange({
                          text: { reversePath: event.target.checked },
                        })}
                      />
                      Reverse text path
                    </label>
                  </>
                ) : null}
              </Section>
            ) : null}
          </>
        ) : null}

        {tab === 'symbol' ? (
          <>
            {graphic.category === 'text' ? (
              <>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => setEditingTextSymbol(true)}
                  style={{ ...field, cursor: disabled ? 'not-allowed' : 'pointer' }}
                >
                  Edit full text symbol…
                </button>
                <Section title="Text frame">
                  {colorField('Frame fill', graphic.fill.color, (color) => onChange({ fill: { color } }))}
                  {numberField('Fill opacity', graphic.fill.opacityPercent, 0, 100, (opacityPercent) =>
                    onChange({ fill: { opacityPercent } }), 1)}
                  {fillRuleField(graphic.fill)}
                  {strokeFields(graphic.stroke, (stroke) => onChange({ stroke }))}
                </Section>
              </>
            ) : null}
            {graphic.category === 'point' ? (
              <Section title="Marker" open>
                <label style={row}>
                  <span>Form</span>
                  <select
                    aria-label="Marker form"
                    value={graphic.marker.shape}
                    disabled={disabled}
                    onChange={(event) => onChange({
                      marker: { shape: event.target.value as typeof graphic.marker.shape },
                    })}
                    style={field}
                  >
                    {MARKER_SHAPES.map((shape) => (
                      <option key={shape} value={shape}>{shape}</option>
                    ))}
                  </select>
                </label>
                {colorField('Marker fill', graphic.marker.fill.color, (color) =>
                  onChange({ marker: { fill: { ...graphic.marker.fill, color } } }))}
                {numberField('Marker fill opacity', graphic.marker.fill.opacityPercent, 0, 100, (opacityPercent) =>
                  onChange({ marker: { fill: { ...graphic.marker.fill, opacityPercent } } }), 1)}
                {colorField('Marker outline', graphic.marker.stroke.color, (color) =>
                  onChange({ marker: { stroke: { ...graphic.marker.stroke, color } } }))}
                {numberField('Marker outline width', graphic.marker.stroke.widthPt, 0, 100, (widthPt) =>
                  onChange({ marker: { stroke: { ...graphic.marker.stroke, widthPt } } }))}
                {numberField('Marker outline opacity', graphic.marker.stroke.opacityPercent, 0, 100, (opacityPercent) =>
                  onChange({ marker: { stroke: { ...graphic.marker.stroke, opacityPercent } } }), 1)}
                <label style={row}>
                  <span>Outline style</span>
                  <select
                    aria-label="Marker outline style"
                    value={graphic.marker.stroke.style}
                    disabled={disabled}
                    onChange={(event) => onChange({
                      marker: {
                        stroke: {
                          ...graphic.marker.stroke,
                          style: event.target.value as LayoutGraphicStroke['style'],
                        },
                      },
                    })}
                    style={field}
                  >
                    {STROKE_STYLES.map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </label>
                {numberField('Marker size', graphic.marker.sizePt, 1, 500, (sizePt) =>
                  onChange({ marker: { sizePt } }))}
                {numberField('Marker angle', graphic.rotation, -360, 360, (rotation) =>
                  onChange({ rotation }), 1)}
              </Section>
            ) : null}
            {graphic.category === 'line' ? (
              <Section title="Line symbol" open>
                {strokeFields(graphic.stroke, (stroke) => onChange({ stroke }))}
              </Section>
            ) : null}
            {graphic.category === 'polygon' || graphic.category === 'arrow' ? (
              <>
                <Section title="Fill" open>
                  {colorField('Fill color', graphic.fill.color, (color) => onChange({ fill: { color } }))}
                  {numberField('Fill opacity', graphic.fill.opacityPercent, 0, 100, (opacityPercent) =>
                    onChange({ fill: { opacityPercent } }), 1)}
                  {fillRuleField(graphic.fill)}
                </Section>
                <Section title="Outline" open>
                  {strokeFields(graphic.stroke, (stroke) => onChange({ stroke }))}
                </Section>
              </>
            ) : null}
            {graphic.category === 'arrow' || graphic.category === 'line' ? (
              <Section title="Arrowheads" open>
                {(['start', 'end'] as const).map((end) => (
                  <label key={end} style={row}>
                    <span>{end === 'start' ? 'Start style' : 'End style'}</span>
                    <select
                      aria-label={`${end === 'start' ? 'Start' : 'End'} arrowhead style`}
                      value={graphic.arrowheads[end]}
                      disabled={disabled}
                      onChange={(event) => onChange({
                        arrowheads: { [end]: event.target.value },
                      })}
                      style={field}
                    >
                      <option value="none">None</option>
                      <option value="open">Open</option>
                      <option value="closed">Closed</option>
                    </select>
                  </label>
                ))}
                {numberField('Arrowhead size', graphic.arrowheads.sizePt, 1, 100, (sizePt) =>
                  onChange({ arrowheads: { sizePt } }))}
              </Section>
            ) : null}
            {'shape' in graphic ? (
              <Section title="Shape parameters">
                {graphic.graphicType === 'roundedRectangle'
                  ? numberField('Corner radius', graphic.shape.cornerRadius, 0, 0.5, (cornerRadius) =>
                    onChange({ shape: { cornerRadius } }), 0.01)
                  : null}
                {graphic.graphicType === 'cloud'
                  ? numberField('Cloud lobes', graphic.shape.cloudLobes, 3, 40, (cloudLobes) =>
                    onChange({ shape: { cloudLobes } }), 1)
                  : null}
                {graphic.graphicType === 'arc'
                  ? <>
                      {numberField('Arc start', graphic.shape.arcStartDeg, -360, 360, (arcStartDeg) =>
                        onChange({ shape: { arcStartDeg } }), 1)}
                      {numberField('Arc end', graphic.shape.arcEndDeg, -360, 360, (arcEndDeg) =>
                        onChange({ shape: { arcEndDeg } }), 1)}
                    </>
                  : null}
                {graphic.graphicType.includes('curved')
                  ? numberField('Curve tension', graphic.shape.tension, 0, 1, (tension) =>
                    onChange({ shape: { tension } }), 0.05)
                  : null}
              </Section>
            ) : null}
          </>
        ) : null}

        {tab === 'placement' ? (
          <Section title={`Position and size (${unitLabel})`} open>
            {(['x', 'y', 'width', 'height'] as const).map((key) => (
              <div key={key}>
                {numberField(
                  `${key === 'x' || key === 'y' ? key.toUpperCase() : `${key[0].toUpperCase()}${key.slice(1)}`} (${unitLabel})`,
                  placementValue(graphic.rect[key]),
                  key === 'width' || key === 'height' ? 0.1 / unitFactor : 0,
                  (key === 'x' || key === 'width' ? pageWidthMm : pageHeightMm) / unitFactor,
                  (value) => setPlacement(key, value),
                  0.1,
                )}
              </div>
            ))}
            {numberField('Rotation', graphic.rotation, -360, 360, (rotation) =>
              onChange({ rotation }), 1)}
            <label style={check}>
              <input
                type="checkbox"
                checked={preserveAspect}
                disabled={disabled}
                onChange={(event) => setPreserveAspect(event.target.checked)}
              />
              Preserve aspect ratio
            </label>
          </Section>
        ) : null}
      </div>
    </aside>
  );
}
